import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Search, Shield, CheckCircle2, MapPin, Star, Users as UsersIcon, Briefcase, Bot, Send, Loader2, X, MessageSquare, Award, UserPlus, Heart } from 'lucide-react';
import { AinaaBadge } from '../components/AinaaBadge';
import { authHeaders } from '../contexts/AuthContext';
import { demoPeople, suggestedPrompts } from '../data/dummyData';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import ProfileDrawer from '../components/ProfileDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const roles = ['All', 'Cinematographer', 'Editor', 'UI Designer', 'Creative Producer', 'Line Producer', 'Brand Strategist', 'Motion Designer', 'Music Composer', 'Voice Artist', 'Photographer', 'Production Designer', 'Sound Designer', 'Screenwriter', 'Director', 'Costume Designer', 'Colorist', 'Casting Director', 'VFX Supervisor', 'Choreographer', 'Storyboard Artist', 'Gaffer', 'Creative Director', 'DIT / Data Wrangler', 'Documentary Producer', 'Assistant Director'];

function IntrowPanel({ onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSearch = async (prompt) => {
    const q = prompt || query.trim();
    if (!q) return;
    setQuery(q);
    setLoading(true);
    try {
      const res = await fetch(`${API}/introw/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ prompt: q }),
      });
      const data = await res.json();
      setResults(data.results || []);
      setSummary(data.ai_summary || '');
    } catch {
      setResults([]);
      setSummary('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="mb-6 rounded-xl border border-border bg-card overflow-hidden"
      data-testid="introw-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-foreground flex items-center justify-center">
            <Bot className="w-3 h-3 text-background" strokeWidth={2} />
          </div>
          <span className="text-xs font-medium">Find collaborators with Specter</span>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose} data-testid="close-introw-panel">
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </Button>
      </div>

      <div className="p-4">
        <form onSubmit={e => { e.preventDefault(); handleSearch(); }} className="flex gap-2 mb-3">
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Describe who you're looking for..."
            className="h-9 text-sm"
            data-testid="introw-search-input"
          />
          <Button type="submit" size="sm" className="h-9 px-3 gap-1.5" disabled={loading || !query.trim()} data-testid="introw-search-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </form>

        {!results && !loading && (
          <div className="flex flex-wrap gap-1.5">
            {suggestedPrompts.slice(0, 4).map((p) => (
              <button
                key={p}
                onClick={() => handleSearch(p)}
                className="px-2.5 py-1 rounded-full border border-border/60 text-[10px] text-muted-foreground hover:text-foreground hover:border-border transition-all"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="text-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Analyzing your request...</p>
          </div>
        )}

        {!loading && results && (
          <div>
            {summary && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50 mb-3" data-testid="introw-ai-summary">
                <Bot className="w-3.5 h-3.5 text-foreground mt-0.5 flex-shrink-0" strokeWidth={2} />
                <p className="text-xs text-foreground leading-relaxed">{summary}</p>
              </div>
            )}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {results.map(person => (
                <div key={person.person_id} className="flex items-center gap-3 p-3 rounded-lg border border-border/40 hover:border-border transition-all" data-testid={`introw-result-${person.person_id}`}>
                  <img src={person.avatar} alt={person.name} className="w-9 h-9 rounded-full object-cover bg-secondary" onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(person.name)}&background=e5e5e5&color=333&size=80`; }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium truncate">{person.name}</p>
                      {person.verified && <CheckCircle2 className="w-3 h-3 text-foreground flex-shrink-0" strokeWidth={2} />}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      <span className="font-mono">{person.ainaa_id}</span> · {person.role}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[hsl(36,80%,52%)]/15 text-[hsl(36,80%,42%)] text-[9px] font-medium">
                      <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />{person.trust_score}
                    </span>
                    {person.style_match && <span className="text-xs font-medium">{person.style_match}%</span>}
                  </div>
                </div>
              ))}
              {results.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No matches found. Try a different query.</p>}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function PeoplePage() {
  const [people, setPeople] = useState(demoPeople);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('All');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [showIntrow, setShowIntrow] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (role !== 'All') params.set('role', role);
    if (verifiedOnly) params.set('verified', 'true');
    if (availableOnly) params.set('available', 'true');

    fetch(`${API}/people?${params}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (data.length > 0) setPeople(data); })
      .catch(() => {});
  }, [search, role, verifiedOnly, availableOnly]); // API is module constant

  const filtered = people.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.role.toLowerCase().includes(search.toLowerCase());
    const matchRole = role === 'All' || p.role === role;
    const matchVerified = !verifiedOnly || p.verified;
    const matchAvail = !availableOnly || p.available;
    return matchSearch && matchRole && matchVerified && matchAvail;
  });

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="people-page">
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">People</h1>
          <p className="text-sm text-muted-foreground mt-1">Discover collaborators with verified trust scores from AINAA.</p>
        </div>
        <Button
          variant={showIntrow ? 'secondary' : 'outline'}
          size="sm"
          className="gap-2 h-9"
          onClick={() => setShowIntrow(!showIntrow)}
          data-testid="toggle-introw-btn"
        >
          <Bot className="w-4 h-4" strokeWidth={2} />
          {showIntrow ? 'Hide Specter' : 'Find with Specter'}
        </Button>
      </motion.div>

      <AnimatePresence>
        {showIntrow && <IntrowPanel onClose={() => setShowIntrow(false)} />}
      </AnimatePresence>

      {/* Filters */}
      <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-xl border border-border/50 bg-card">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, role or skill..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="search-people-input" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-48 h-9" data-testid="filter-role-select"><SelectValue /></SelectTrigger>
          <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch id="verified" checked={verifiedOnly} onCheckedChange={setVerifiedOnly} data-testid="filter-verified-switch" />
          <Label htmlFor="verified" className="text-xs cursor-pointer">Verified only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="available" checked={availableOnly} onCheckedChange={setAvailableOnly} data-testid="filter-available-switch" />
          <Label htmlFor="available" className="text-xs cursor-pointer">Available only</Label>
        </div>
      </motion.div>

      {/* People Grid */}
      <TooltipProvider delayDuration={200}>
      <motion.div variants={fadeUp} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(person => (
          <div
            key={person.person_id}
            className="p-5 rounded-xl border border-border/50 bg-card hover:border-border transition-all duration-200 group cursor-pointer"
            onClick={() => setSelectedPerson(person.person_id)}
            data-testid={`person-card-${person.person_id}`}
          >
            <div className="flex items-start gap-3 mb-3">
              <img
                src={person.avatar}
                alt={person.name}
                className="w-11 h-11 rounded-full object-cover bg-secondary"
                onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(person.name)}&background=e5e5e5&color=333&size=96`; }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-medium truncate">{person.name}</h3>
                  {person.verified && <CheckCircle2 className="w-3.5 h-3.5 text-foreground flex-shrink-0" strokeWidth={2} />}
                </div>
                <p className="text-xs text-muted-foreground">{person.role}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Chat icon */}
                <button
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-secondary transition-colors relative"
                  onClick={(e) => { e.stopPropagation(); setSelectedPerson(person.person_id); }}
                  data-testid={`chat-${person.person_id}`}
                >
                  <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                  {person.connection_status === 'accepted' && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full" />
                  )}
                </button>
                {/* Trust Score Amber Pill */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[11px] font-semibold cursor-default tabular-nums">
                      {person.trust_score}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="p-3 max-w-[220px]" data-testid={`trust-tooltip-${person.person_id}`}>
                    <p className="text-[10px] font-semibold mb-2 text-foreground">Trust Dimensions</p>
                    <div className="space-y-1.5">
                      {[
                        { label: 'Reliability', val: person.reliability },
                        { label: 'Skill', val: person.skill_score },
                        { label: 'Professionalism', val: person.decency },
                        { label: 'Discipline', val: person.discipline },
                      ].map(d => (
                        <div key={d.label} className="flex items-center gap-2">
                          <span className="text-[9px] text-muted-foreground w-[72px]">{d.label}</span>
                          <div className="flex-1 h-1 rounded-full bg-border">
                            <div className="h-full rounded-full" style={{
                              width: `${d.val || 0}%`,
                              backgroundColor: (d.val || 0) >= 85 ? '#10B981' : (d.val || 0) >= 70 ? '#F59E0B' : '#EF4444'
                            }} />
                          </div>
                          <span className="text-[9px] font-medium w-5 text-right tabular-nums">{d.val || 0}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[9px] text-muted-foreground/60 mt-2 italic">Powered by Specter</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Key info row */}
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-3">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} /> {person.location}</span>
              <span className="text-foreground font-medium">{person.rate}</span>
            </div>

            {/* Lobby enrichment row */}
            <div className="flex items-center gap-2 mb-3">
              {person.trust_tier && (
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${
                  person.trust_tier === 'Exceptional' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' :
                  person.trust_tier === 'Strong' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' :
                  person.trust_tier === 'Good' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                  'bg-zinc-500/10 text-zinc-500 border-zinc-500/20'
                }`} data-testid={`trust-tier-${person.person_id}`}>
                  <Shield className="w-2.5 h-2.5" strokeWidth={2} />{person.trust_tier}
                </span>
              )}
              {person.endorsement_count > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-600 text-[9px] font-medium border border-purple-500/20" data-testid={`endorsements-${person.person_id}`}>
                  <Award className="w-2.5 h-2.5" strokeWidth={2} />{person.endorsement_count} endorsed
                </span>
              )}
              {person.is_following && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 text-[9px] font-medium border border-sky-500/20">
                  <Heart className="w-2.5 h-2.5" strokeWidth={2} />Following
                </span>
              )}
            </div>

            {/* Bottom row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                <span>{person.projects_completed} projects</span>
                <span>{person.common_collaborators} mutual</span>
              </div>
              {person.availability_status === 'manageable' ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 font-medium">Manageable</span>
              ) : person.available !== false && person.availability_status !== 'busy' ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-medium">Available</span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">Busy</span>
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              {person.tags?.slice(0, 4).map(tag => (
                <Badge key={tag} variant="secondary" className="text-[10px] px-2 py-0 h-5 font-normal">{tag}</Badge>
              ))}
            </div>
          </div>
        ))}
      </motion.div>
      </TooltipProvider>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <UsersIcon className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" strokeWidth={2} />
          <p className="text-sm text-muted-foreground">No collaborators found</p>
        </div>
      )}

      {/* Profile Drawer */}
      <ProfileDrawer
        personId={selectedPerson}
        onClose={() => setSelectedPerson(null)}
        onStartChat={(pid) => {
          setSelectedPerson(null);
          window.location.href = '/messages';
        }}
      />
    </motion.div>
  );
}
